import { useCallback, useEffect, useMemo, useState } from "react";
import SelectIcon from "../../assets/Order/SelectIcon.svg";
import FilterIcon from "../../assets/Order/FilterIcon.svg";
import { ScrollNav } from "../../components/ScrollNav";
import { CustomTable } from "../../components/Table";
import { SearchBox } from "../../components/SearchBox";
import { useNavigate } from "react-router-dom";
import { ResponsiveState } from "../../utils/responsiveState";
import {
  PassbookHistory,
  PassbookColumns,
  DEFAULT_WALLET_API_SORT,
  walletTxSortToPayload,
  type WalletTxSortKey,
  type WalletApiSortState,
} from "./history/passbookHistory";
import { CashbackHistory } from "./history/cashbackHistory";
import { OnlineDetailsColumns } from "./history/onlineHistory";
import { cashbackDetailsColumns } from "./history/cashbackHistory";
import { POST } from "../../utils/webService";
import {
  GET_WALLET_TRANSACTION,
  GET_WALLET_TRANSACTION_FINANCE,
} from "../../utils/ApiUrls";
import { toast } from "react-hot-toast";
import { Breadcrum } from "../../components/Layout/breadcrum";
import { Spinner } from "../../components/Spinner";
import { useSelector } from "react-redux";
import AccessDenied from "../../components/AccessDenied";
import Pagination from "../../components/OnePagination/OnePagination";
import { checkPageAuthorized } from "../../redux/reducers/role";
import DeleteIconForLg from "../../assets/DeleteIconRedColor.svg";
import editIcon from "../../assets/serv/edit.svg";
import CloseIcon from "../../assets/CloseIcon.svg";
import { useErrorBoundary } from "react-error-boundary";
import { capitalizeFirstLetter } from "../../utils/utility";
import { createColumnHelper } from "@tanstack/react-table";
import { Approved } from "./StatusComponents";
import CopyTooltip from "../../components/CopyToClipboard";
import {
  date_DD_MMM_YYYY_HH_MM,
  date_DD_MMM_YYYY_HH_MM_IST,
  date_DD_MMM_YYYY_HH_MM_SS,
} from "../../utils/dateFormater";
import { NewSearch } from "../../components/NewSearch/NewSearch";
import { PathFinder } from "../../utils/Helper/PathFinder";
import { inputRegexFilter } from "../../utils/Helper/Filter";
import DatePicker from "react-datepicker";
import DateButton from "../../components/Button/DateButton";
import OneButton from "../../components/Button/OneButton";
import RightSideModal from "../../components/CustomModal/customRightModal";
import TransactionFilter from "./transactionFilter";

const arrayData = [
  { label: "Passbook" },
  // { label: "Cashback" },
  { label: "NEFT/IMPS/RTGS Transaction" },
  { label: "Cashback" },
];

const DEFAULT_SORT_DIRECTION_BY_FIELD: Record<WalletTxSortKey, 1 | -1> = {
  _id: -1,
  balance: 1,
  credited: 1,
  debited: 1,
};

const CASHBACK_SORT_PAYLOAD_FIELDS = {
  amountKey: "yaariCash",
  balanceKey: "yaariCashBalance",
};

const getEmptyTransactionFilterState = () => ({
  name: "",
  menu: [] as any[],
  label: "",
  isCollapse: false,
});

const getEmptyTransactionFilterPayload = () => ({
  filterArrOne: [] as any[],
});

const getEmptyPersistFilterData = () => ({
  deliveryPincode: [] as any[],
  pickupPincode: [] as any[],
  status: [] as any[],
  description: [] as any[],
});

export const Transaction = () => {
  const [apiSort, setApiSort] = useState<WalletApiSortState>(
    DEFAULT_WALLET_API_SORT,
  );
  const { showBoundary } = useErrorBoundary() || {};

  const navigate = useNavigate();
  const roles = useSelector((state: any) => state?.roles);
  const isActive = checkPageAuthorized("Transaction History");
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [renderingComponents, setRenderingComponents] = useState(0);
  const { isLgScreen } = ResponsiveState();
  const [loading, setLoading] = useState(false);
  const [data, setData]: any = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowSelectedData, setRowSelectedData]: any = useState([]);

  const [searchValue, setSearchValue]: any = useState("");
  const currenturl = window.location.href;
  const path = PathFinder(currenturl);
  const [dateRange, setDateRange]: any = useState([null, null]);
  let thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const [startDate, setStartDate] = useState<any>(thirtyDaysAgo);
  const [endDate, setEndDate] = useState<any>(new Date());
  const [filterModal, setFilterModal] = useState(false);
  const [defaultOpenFilter, setDefaultOpenFilter] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [isFilterLoading, setIsFilterLoading] = useState<any>(false);
  const [filterState, setFilterState] = useState(
    getEmptyTransactionFilterState(),
  );
  const [filterPayLoad, setFilterPayLoad] = useState(
    getEmptyTransactionFilterPayload(),
  );

  const [persistFilterData, setPersistFilterData]: any = useState(
    getEmptyPersistFilterData(),
  );

  const openFilterModal = useCallback(() => {
    setDefaultOpenFilter("");
    setFilterModal(true);
  }, []);

  const openStatusFilterModal = useCallback(() => {
    setDefaultOpenFilter("Status");
    setFilterModal(true);
  }, []);

  const clearTransactionFilters = useCallback((nextDefaultOpenFilter = "") => {
    setDefaultOpenFilter(nextDefaultOpenFilter);
    setFilterState(getEmptyTransactionFilterState());
    setFilterPayLoad(getEmptyTransactionFilterPayload());
    setPersistFilterData(getEmptyPersistFilterData());
    setFilterResetKey((prev) => prev + 1);
  }, []);

  const cycleWalletSort = useCallback((field: WalletTxSortKey) => {
    setApiSort((prev) => {
      if (prev.key !== field) {
        return { key: field, dir: DEFAULT_SORT_DIRECTION_BY_FIELD[field] };
      }

      return { key: field, dir: prev.dir === 1 ? -1 : 1 };
    });
    setCurrentPage(1);
  }, []);

  const passbookColumns = useMemo(
    () =>
      PassbookColumns({
        apiSort,
        cycleWalletSort,
        onStatusFilterClick: openStatusFilterModal,
        statusFilterActive: (persistFilterData?.status || []).length > 0,
      }),
    [
      apiSort.key,
      apiSort.dir,
      cycleWalletSort,
      openStatusFilterModal,
      persistFilterData?.status,
    ],
  );

  const cashbackColumns = useMemo(
    () =>
      cashbackDetailsColumns({
        apiSort,
        cycleWalletSort,
        onStatusFilterClick: openStatusFilterModal,
        statusFilterActive: (persistFilterData?.status || []).length > 0,
      }),
    [
      apiSort.key,
      apiSort.dir,
      cycleWalletSort,
      openStatusFilterModal,
      persistFilterData?.status,
    ],
  );

  const resetAllFilters = () => {
    clearTransactionFilters("Status");
    setCurrentPage(1);
    // Trigger normal data refresh via existing useEffect dependency
    setIsFilterLoading(true);
    setTimeout(() => setIsFilterLoading(false), 0);
  };
  const getData = async () => {
    const payload: any = {
      filter: {},
      skip: searchValue !== "" ? 0 : (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      pageNo: currentPage,
      sort: walletTxSortToPayload(
        apiSort,
        renderingComponents === 2 ? CASHBACK_SORT_PAYLOAD_FIELDS : {},
      ),
      search: searchValue,
      // apiType: renderingComponents === 1 ? "neft/rtgs/imps" : "",
    };

    if (startDate && endDate) {
      let startEpoch = null;
      let lastendEpoch = null;

      if (startDate instanceof Date && endDate instanceof Date) {
        startDate.setHours(0, 0, 0, 0);
        startEpoch = startDate.getTime();

        endDate.setHours(23, 59, 59, 999);
        const endEpoch = endDate.getTime();

        lastendEpoch = endEpoch;
      }
      payload.filter.startDate = startEpoch;
      payload.filter.endDate = lastendEpoch;

      // payload?.filter?.push({
      //   createdAt: {
      //     $gte: startEpoch,
      //     $lte: lastendEpoch,
      //   },
      // });
    }
    payload.filter.type =
      renderingComponents === 1
        ? "neft"
        : (payload.filter.type = renderingComponents === 2 ? "cashback" : "");
    let extraFilter = filterPayLoad?.filterArrOne;
    extraFilter?.map((el: any, i: any) => {
      if (el?.status?.$in?.length > 0) {
        payload.filter.status = el.status;
      }
      if (el?.description?.$in?.length > 0) {
        payload.filter.description = el.description;
      }
    });

    // const { data: response } = await inputRegexFilter(
    //   searchValue,
    //   path,
    //   payload
    // );
    const { data: response } = await POST(
      GET_WALLET_TRANSACTION_FINANCE,
      payload,
    );
    // console.log("🚀 ~ getData ~ response:", response);
    if (response?.success) {
      setData(response?.data || []);
      setTotalItemCount(response.total);
      setLoading(false);
      setIsFilterLoading(false);
      setFilterModal(false);
    } else {
      toast.error(response?.message);
      // showBoundary(response?.message);
      setLoading(false);
      setIsFilterLoading(false);
    }
  };

  useEffect(() => {
    // if (renderingComponents === 0 || renderingComponents === 2) {
    setLoading(true);
    // console.log("renderingComponents", renderingComponents);

    getData();
    // }
  }, [
    itemsPerPage,
    currentPage,
    apiSort.key,
    apiSort.dir,
    searchValue,
    isFilterLoading,
    renderingComponents,
    startDate,
    endDate,
  ]);

  // useEffect(() => {
  //   setLoading(true);

  //   const getData = setTimeout(async () => {
  //     let skip;
  //     let limit;
  //     let pageNo;
  //     if (currentPage !== 1) {
  //       skip = 0;
  //       limit = itemsPerPage;
  //       pageNo = 1;
  //     } else {
  //       skip = (currentPage - 1) * itemsPerPage;
  //       limit = itemsPerPage;
  //       pageNo = currentPage;
  //     }
  //     const payload: any = {
  //       filter: [],
  //       skip,
  //       limit,
  //       pageNo,
  //       sort: { _id: sortOrder === "desc" ? -1 : 1 },
  //       searchValue: searchValue,
  //     };

  //     if (startDate && endDate) {
  //       let startEpoch = null;
  //       let lastendEpoch = null;

  //       if (startDate instanceof Date && endDate instanceof Date) {
  //         startDate.setHours(0, 0, 0, 0);
  //         startEpoch = startDate.getTime();

  //         endDate.setHours(23, 59, 59, 999);
  //         const endEpoch = endDate.getTime();

  //         lastendEpoch = endEpoch;
  //       }
  //       payload?.filter?.push({
  //         createdAt: {
  //           $gte: startEpoch,
  //           $lte: lastendEpoch,
  //         },
  //       });
  //     }
  //     let extraFilter = filterPayLoad?.filterArrOne;
  //     extraFilter?.map((el: any, i: any) => {
  //       if (el?.status?.$in?.length > 0) {
  //         payload?.filter?.push(el);
  //       }
  //       if (el?.description?.$in?.length > 0) {
  //         payload?.filter?.push(el);
  //       }
  //     });

  //     if (renderingComponents === 2) {
  //       payload.filter.type = "WALLET_RECHARGE_USING_NEFT";
  //     }

  //     const { data: response } = await inputRegexFilter(
  //       searchValue,
  //       path,
  //       payload
  //     );

  //     if (response?.success) {
  //       setData(response?.data || []);
  //       setTotalItemCount(response.totalTransactions);
  //       setLoading(false);
  //       setIsFilterLoading(false);
  //       setFilterModal(false);
  //     } else {
  //       toast.error(response?.message);
  //       // showBoundary(response?.message);
  //       setLoading(false);
  //     }
  //   }, 500);

  //   return () => clearTimeout(getData);
  // }, [endDate]);

  const PersistFilterArr = (key: any, data: any) => {
    setPersistFilterData((prevData: any) => {
      return { ...prevData, [key]: [...data] };
    });
  };

  function getObjectWithIsActiveTrue(data: any, name: any) {
    let tempArrOne = filterPayLoad?.filterArrOne;

    const updateFilterArr = (arr: any, key: any, subKey: any, data: any) => {
      const index = arr.findIndex(
        (findArr: any) => Object.keys(findArr)[0] === key,
      );
      if (index > -1) {
        arr[index][key][subKey] = data;
      } else {
        const newObj = { [key]: { [subKey]: [...data] } };
        arr.push(newObj);
      }
    };

    switch (name) {
      case "Status":
        updateFilterArr(tempArrOne, "status", "$in", data);
        PersistFilterArr("status", data);
        break;
      case "Description":
        updateFilterArr(tempArrOne, "description", "$in", data);
        PersistFilterArr("description", data);
        break;
      default:
        break;
    }

    tempArrOne = tempArrOne.filter((obj: any) => {
      const key = Object.keys(obj)[0];
      return obj[key].$in.length > 0;
    });

    setFilterPayLoad({
      ...filterPayLoad,
      filterArrOne: [...tempArrOne],
    });
  }

  useEffect(() => {
    getObjectWithIsActiveTrue(filterState?.menu, filterState?.name);
  }, [filterState]);

  function formatDate(dateString: any) {
    const date = new Date(dateString);

    // Format date as '23 Aug 2024'
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    // Format time as '04:38 pm'
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
  }

  const handleClear = () => {
    setDateRange([null, null]);
    setStartDate(null);
    setEndDate(null);
  };

  //column for wallet NEFT
  const columnsHelper = createColumnHelper<any>();
  const columns = [
    columnsHelper.accessor(" Courier Partner ID and Name", {
      header: (props: any) => {
        return (
          <div className="flex justify-start items-center min-w-[120px]">
            <h1 className="text-sm font-semibold leading-5 ">Client Id</h1>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const rowData = row?.original;
        return <div className=" flex"> {rowData?.sellerId || "--"}</div>;
      },
    }),
    columnsHelper.accessor("transactionId", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <h1 className="font-semibold leading-5 text-sm">Transaction ID</h1>
          </div>
        );
      },
      cell: (info: any) => {
        return (
          <div className="flex  items-center justify-between ">
            <div className=" w-[80px] whitespace-nowrap  overflow-hidden overflow-ellipsis font-Open font-normal leading-5 text-sm   ">
              {info.row.original.transactionId}
            </div>
            <div className="cursor-pointer">
              <CopyTooltip
                stringToBeCopied={`${info.row.original.transactionId}
               `}
              />
            </div>
          </div>
        );
      },
    }),

    columnsHelper.accessor("noOfAwb", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <h1>Ref/UTR No.</h1>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const rowData = row?.original;
        const utrNo = rowData?.description.split(" ").pop();

        return (
          <div className="flex justify-between">
            <div>{utrNo}</div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("uniqueCourierPartners", {
      header: () => {
        return (
          <div className="flex justify-between min-w-[120px]">
            <h1>Amount</h1>
          </div>
        );
      },
      cell: ({ row }) => {
        const rowData = row?.original;

        return (
          <div className="flex space-x-2 items-center">
            <div>₹ {rowData?.amount}</div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("charges", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px]">
            <div className="flex flex-col">Type</div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const rowData = row?.original;

        return (
          <div className="flex justify-center flex-col p-3 gap-y-2">Credit</div>
        );
      },
    }),
    columnsHelper.accessor("finalAmount", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <div className="flex flex-col">
              <h1>Discription</h1>
            </div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const rowData = row?.original;

        return (
          <div className="flex justify-center flex-col min-w-[120px]">
            <div>{rowData?.description}</div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("cashBackSS", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <div className="flex flex-col">CashBack</div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        return (
          <div className="flex justify-between">
            <div> 0 </div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("req,Date", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <div className="flex flex-col">Requested Date</div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const rowData = row?.original;
        const date: any = new Date(rowData?.createdAt);

        const formattedDateTime = date_DD_MMM_YYYY_HH_MM(date);

        return (
          <div className="flex justify-between">
            <div className="flex flex-col  whitespace-nowrap my-4 ">
              <span>{formattedDateTime.split(",")[0]}</span>
              <span>{formattedDateTime.split(",")[1]}</span>
            </div>
          </div>
        );
      },
    }),
    columnsHelper.accessor("cashBack", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[120px] ">
            <div className="flex flex-col">Approved Date</div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const timestamp = new Date(row.original.updated_At);

        // Adjust for IST (UTC+5:30)
        const istTimestamp = new Date(
          timestamp.getTime() + 5.5 * 60 * 60 * 1000,
        );

        const humanReadableDate = istTimestamp.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        const hours = istTimestamp.getUTCHours();
        const minutes = istTimestamp.getUTCMinutes();
        const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
        const formattedMinutes = minutes.toString().padStart(2, "0");
        const ampm = hours >= 12 ? "pm" : "am";
        const time = `${formattedHours}:${formattedMinutes} ${ampm}`;
        return (
          <div className="flex justify-between">
            <div className="flex flex-col whitespace-nowrap my-4">
              <span>{`${humanReadableDate} ${time}`}</span>
            </div>
          </div>
        );
      },
    }),
    // columnsHelper.accessor("remark", {
    //   header: () => {
    //     return (
    //       <div className="flex justify-between items-center min-w-[120px] ">
    //         <div className="flex flex-col">
    //           <h1> Action By </h1>
    //         </div>
    //       </div>
    //     );
    //   },
    //   cell: ({ row }: any) => {
    //     const rowData = row?.original;

    //     return (
    //       <div className="flex justify-between">
    //         <div> {rowData?.approvedBy || "--"}</div>
    //       </div>
    //     );
    //   },
    // }),
    columnsHelper.accessor("actions", {
      header: () => {
        return (
          <div className="flex items-center min-w-[50px] ">
            <div className="flex flex-col">
              <h1> Status </h1>
            </div>
          </div>
        );
      },
      cell: ({ row }: any) => {
        let rowData = row.original;
        return (
          <div className="flex cursor-pointer">
            <div
              className="border"
              // onClick={() =>
              //   setShowNeftModal({ isOpen: true, modalData: rowData })
              // }
            >
              {rowData?.status === "Requested" ? (
                <span className="border-[0.5px] border-[#F0A22E] bg-[#FDF6EA] text-[#F0A22E] px-3 py-[4px] rounded-sm">
                  {"Requested"}
                </span>
              ) : rowData?.status === "REJECTED" ? (
                <span className="border-[0.5px] border-[#F35838] bg-[#FEEEEB] text-[#F35838] px-3 py-[4px] rounded-sm">
                  {capitalizeFirstLetter(rowData?.status)}
                </span>
              ) : rowData?.status === "SUCCESS" ? (
                <span className="border-[0.5px] border-[#7CCA62] bg-[#F2FAEF] text-[#7CCA62] px-3 py-[4px] rounded-sm">
                  {"Approved"}
                </span>
              ) : (
                "N/A"
              )}
            </div>
          </div>
        );
      },
    }),
  ];

  const onPageIndexChange = (paginationData: any) => {
    setCurrentPage(paginationData.currentPage);
  };

  const onPerPageItemChange = (paginationData: any) => {
    setItemsPerPage(paginationData.itemsPerPage);
    setCurrentPage(1);
  };

  const setScrollIndex = (id: number) => {
    if (id === renderingComponents) return;

    clearTransactionFilters();
    setFilterModal(false);
    setCurrentPage(1);
    setRenderingComponents(id);
  };

  let debounceTimer: any;
  // const handleSearch = (value: string) => {
  //   setDebouncedSearchValue(value);
  // };

  const handleSearch = async (value: string) => {
    setSearchValue(value);
  };

  let params = ["transactionId"];

  const filterButton = () => {
    const actionHandler = () => {
      // console.log("rowSelectedData", rowSelectedData);
    };

    if (isLgScreen) {
      return (
        <div className="grid grid-cols-3 gap-x-2 lg:flex">
          <div>
            {/* <TransactionSearchBox
              label="Search"
              value={debouncedSearchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleSearch(e.target.value);
              }}
              customPlaceholder="Search By Transaction Id"
              getFullContent={clearSearchValue}
            /> */}
            <SearchBox
              label="Search"
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleSearch(e.target.value);
              }}
              customPlaceholder="Search by awb / order ID"
              getFullContent={() => setSearchValue("")}
            />

            {/* <NewSearch
              page="/wallet/transaction-history"
              params={params}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearch(e.target.value)
              }
              value={searchValue}
            /> */}
          </div>
          {rowSelectedData?.length > 0 && (
            <div className=" flex items-center ">
              <div className="rounded-md p-1 flex border border-[#A4A4A4]">
                <div
                  className="border-r border-[#A4A4A4]  px-3 py-1 flex items-center rounded-l-md cursor-pointer"
                  onClick={actionHandler}
                >
                  <img src={DeleteIconForLg} alt="" className=" w-[17px]" />
                </div>
                <div
                  className="px-3 py-1 flex items-center rounded-r-md cursor-pointer"
                  onClick={actionHandler}
                >
                  <img src={editIcon} alt="" className=" w-[17px]" />
                </div>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="grid grid-cols-3 gap-x-2">
          <div className="flex items-center justify-center border-[1px] rounded-md border-[#A4A4A4] col-span-2">
            <img src={SelectIcon} alt="" />
            <span className="ml-2 text-[#1C1C1C] text-[14px] font-medium">
              SELECT
            </span>
          </div>
          <div
            className="grid justify-center items-center border-[1px] rounded-md border-[#A4A4A4] "
            onClick={() => {
              navigate("/neworder/filter");
            }}
          >
            <img src={FilterIcon} alt="Filter Order" width="16px" />
          </div>
        </div>
      );
    }
  };

  const applyFilterforTransaction = () => {
    setCurrentPage(1);
    setIsFilterLoading(true);
  };

  const renderMobileCards = () => {
    if (!data || data?.length === 0) return null;

    // 0: Passbook, 1: NEFT/IMPS/RTGS, 2: Cashback
    if (renderingComponents === 2) {
      return (
        <div className="mt-4 space-y-4">
          {data.map((row: any, index: number) => {
            const formatted = date_DD_MMM_YYYY_HH_MM(row?.createdAt);
            const [datePart = "--", timePart = "--"] =
              String(formatted).split(",");

            return (
              <CashbackHistory
                key={`${index}_${row?.transactionId || row?._id || "row"}`}
                data={{
                  title: row?.transactionId || "--",
                  rupee: String(row?.yaariCash ?? row?.amount ?? "--"),
                  date: datePart?.trim(),
                  time: timePart?.trim(),
                  description: row?.description || "--",
                  type: row?.type,
                  yaariCashBalance: row?.yaariCashBalance,
                  transactionId: row?.transactionId,
                  status: row?.status,
                  remark: row?.remark,
                }}
              />
            );
          })}
        </div>
      );
    }

    // Passbook + NEFT: use existing passbook-style card
    return (
      <div className="mt-4 space-y-4">
        {data.map((row: any, index: number) => (
          <PassbookHistory
            key={`${index}_${row?.transactionId || row?._id || "row"}`}
            data={{
              title: row?.transactionId || "--",
              rupee: String(row?.amount ?? "--"),
              date: row?.createdAt,
              credited:
                row?.type === "credit" ? String(row?.amount ?? "0") : "0",
              debited: row?.type === "debit" ? String(row?.amount ?? "0") : "0",
              balance: row?.balance,
              status: row?.status,
              paymentGatewayName: row?.paymentGatewayName,
              privateCompanyId: row?.privateCompanyId,
              remark: row?.remark,
              discription: row?.description,
              orderId: row?.orderId,
              amount: row?.amount,
              slabAmount: "0",
              redeemAmount: "0",
              redeemPoint: "0",
              type: row?.type,
            }}
          />
        ))}
      </div>
    );
  };

  const render = () => {
    if (renderingComponents === 0) {
      return (
        <div>
          {isLgScreen ? (
            <CustomTable
              rowData={data || []}
              columnsData={passbookColumns}
              setRowSelectedData={setRowSelectedData}
            />
          ) : (
            <div className="lg:hidden">{renderMobileCards()}</div>
          )}
        </div>
      );
    } else if (renderingComponents === 1) {
      return (
        <div>
          {isLgScreen ? (
            <CustomTable
              rowData={data || []}
              columnsData={passbookColumns}
              setRowSelectedData={setRowSelectedData}
            />
          ) : (
            <div className="lg:hidden">{renderMobileCards()}</div>
          )}
        </div>
      );
    } else if (renderingComponents === 2) {
      return (
        <div>
          {isLgScreen ? (
            <CustomTable rowData={data || []} columnsData={cashbackColumns} />
          ) : (
            <div className="lg:hidden">{renderMobileCards()}</div>
          )}
        </div>
      );
    }
  };

  // const errorFun = () => {
  //   throw new Error("Hii");
  // };

  return (
    <>
      {isActive ? (
        <>
          <div>
            <Breadcrum label="Transaction History" />
          </div>

          <div className="flex flex-col">
            <div className="mx-4">
              <div className="lg:flex justify-between lg:mt-2 lg:mb-4">
                <div>
                  <ScrollNav
                    arrayData={arrayData}
                    showNumber={false}
                    setScrollIndex={setScrollIndex}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="">
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update: any) => {
                        setDateRange(update);
                        if (update[0] === null && update[1] === null) {
                          // Explicitly set startDate and endDate to null when cleared
                          setStartDate(null);
                          setEndDate(null);
                          // fetchCodRemittanceData();
                        } else {
                          // Update startDate and endDate based on the selected range
                          setStartDate(update[0]);
                          setEndDate(update[1]);
                        }
                      }}
                      // isClearable={true}
                      dateFormat="dd/MM/yyyy"
                      customInput={
                        <DateButton
                          text="Select From & To Date" // Text for the button
                          onClick={() => {}} // onClick is managed by DatePicker
                          className="h-[36px]"
                          value={
                            startDate && endDate
                              ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                              : ""
                          } // Display date range
                          onClear={handleClear} // Handle clear action
                        />
                      } // Include placeholder onClick function
                    />
                  </div>
                  <div className="hidden lg:block">{filterButton()}</div>
                  <div className="relative ml-2">
                    <OneButton
                      text="FILTER"
                      onClick={openFilterModal}
                      variant="quad"
                      showIcon={true}
                      icon={FilterIcon}
                      className="!uppercase"
                    />
                    {(persistFilterData?.status?.length || 0) +
                      (persistFilterData?.description?.length || 0) >
                      0 && (
                      <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] leading-[18px] font-semibold text-center">
                        {(persistFilterData?.status?.length || 0) +
                          (persistFilterData?.description?.length || 0)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* <div className="grid grid-cols-2 justify-center mt-4 h-[36px] lg:hidden">
                  <div className="flex items-center"></div>
                  <div className="flex items-center justify-end gap-x-2">
                    <div className="flex items-center justify-center border-[1px] py-2 px-4 rounded-md border-[#A4A4A4] col-span-2">
                      <img src={SelectIcon} alt="" />
                      <span className="ml-2 text-[#1C1C1C] text-[14px] font-medium">
                        SELECT
                      </span>
                    </div>
                  </div>
                </div> */}

              {loading ? (
                <div className="flex justify-center  items-center h-[65vh]">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div>{<div className="">{render()}</div>}</div>

                  {totalItemCount > 0 ? (
                    <Pagination
                      totalItems={totalItemCount}
                      itemsPerPageOptions={[
                        10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000,
                      ]}
                      onPageChange={onPageIndexChange}
                      onItemsPerPageChange={onPerPageItemChange}
                      pageNo={currentPage}
                      initialItemsPerPage={itemsPerPage}
                    />
                  ) : (
                    <div className="flex items-center justify-center py-10 text-sm text-[#666]">
                      No data found
                    </div>
                  )}
                </>
              )}
            </div>
            {/* filter */}
            <RightSideModal
              isOpen={filterModal}
              onClose={() => {
                setFilterModal(false);
              }}
              className="!justify-start"
              showMobileCancelButton={false}
            >
              <div className="h-full flex flex-col">
                <div className="flex justify-between items-center gap-3 px-5 py-4 border-b border-gray-200 bg-white">
                  <p className="text-lg sm:text-xl font-semibold text-[#1C1C1C]">
                    Filter
                  </p>
                  <button
                    type="button"
                    onClick={() => setFilterModal(false)}
                    className="text-gray-600 hover:text-gray-800 text-2xl leading-none flex-shrink-0"
                    aria-label="Close filter"
                  >
                    ×
                  </button>
                </div>

                <div className="flex-1 overflow-auto px-5 py-4 bg-gray-50">
                  <TransactionFilter
                    filterState={filterState}
                    setFilterState={setFilterState}
                    setFilterPayLoad={setFilterPayLoad}
                    filterPayLoad={filterPayLoad}
                    filterModal={filterModal}
                    defaultOpenFilter={defaultOpenFilter}
                    filterResetKey={filterResetKey}
                    setPersistFilterData={setPersistFilterData}
                    persistFilterData={persistFilterData}
                  />
                </div>

                <div className="border-t border-gray-200 bg-white p-4 flex gap-3">
                  <OneButton
                    text="RESET ALL"
                    onClick={() => {
                      resetAllFilters();
                    }}
                    className="w-full !rounded-full !px-4 !py-2 !text-sm"
                    variant="secondary"
                  />
                  {isFilterLoading ? (
                    <div className="w-full flex justify-center items-center">
                      <Spinner />
                    </div>
                  ) : (
                    <OneButton
                      text="APPLY"
                      onClick={applyFilterforTransaction}
                      className="w-full !rounded-full !px-4 !py-2 !text-sm"
                      variant="primary"
                    />
                  )}
                </div>
              </div>
            </RightSideModal>
          </div>
        </>
      ) : (
        <div>
          <AccessDenied />
        </div>
      )}
    </>
  );
};
